import { SearchRegistry } from '../types';

export const aiAtRydSearchEntries: SearchRegistry = {
  '/ai-at-ryd/usage-and-limits': {
    keywords: [
      'usage',
      'limits',
      'rate limit',
      'quota',
      'usage and limits',
      'api usage',
      'traffic',
      'data volume',
      'ai tokens',
      'ai agent usage',
      '429',
      'too many requests',
      'retry-after',
      'x-ratelimit',
      'higher limits',
    ],
    questions: [
      'How much of our limits have we used?',
      'Why was my request refused with 429?',
      'Which AI agents use our account?',
      'How do I get higher limits?',
      'What are the limits of my plan?',
      'Are usage limits enforced already?',
    ],
    faqs: [
      {
        q: 'Why did ryd answer a request with HTTP 429?',
        a: 'A usage limit was reached for your company, your user or the AI agent or API client that sent the request. The response carries a Retry-After header; wait that long before retrying. AI at ryd → Usage & Limits shows what is used and when each limit resets.',
      },
      {
        q: 'Are usage limits enforced already?',
        a: 'Not yet. Usage is measured and shown on Usage & Limits, but no request is refused for now. ryd will announce in advance when the limits start to apply.',
      },
      {
        q: 'What are the limits of my plan?',
        a: 'AI at ryd → Usage & Limits lists the limits of every plan (ryd fleet free, plus, enterprise and API partner), per user and per AI agent or API client, with your plan highlighted.',
      },
      {
        q: 'How do I get higher usage limits?',
        a: 'Buying higher limits is not available yet; it is planned for the future. If a limit does not fit how you work, talk to your ryd account manager.',
      },
    ],
  },
  '/ai-at-ryd/overview': {
    keywords: [
      'ai',
      'artificial intelligence',
      'ki',
      'künstliche intelligenz',
      'ai channels',
      'ai at ryd',
      'agents',
      'mcp',
      'a2a',
      'agent to agent',
      'portal assistant',
    ],
    questions: ['How can my company use AI with ryd?', 'Which AI channels does ryd offer?'],
    faqs: [
      {
        q: 'Which AI channels does ryd offer?',
        a: 'The ryd Portal Assistant and the order, fleet and driver APIs and webhooks are available today. Connecting your own AI assistant through MCP is in pilot. Automations, an Agent Kit and agent-to-agent collaboration are available on demand.',
      },
      {
        q: 'Can my AI assistant use ryd verify?',
        a: 'No. ryd verify is in use today, but it is not one of the functions an AI app or agent of a partner can use directly.',
      },
      {
        q: 'Can I use other portal functions through an API?',
        a: 'Yes. Every function of the ryd portal can be offered as an API. ryd extends the APIs on demand; tell your ryd contact which function you need.',
      },
    ],
  },
  '/ai-at-ryd/connect': {
    keywords: [
      'connect chatgpt',
      'connect claude',
      'claude connector',
      'chatgpt connector',
      'microsoft copilot',
      'copilot studio',
      'n8n',
      'mcp server address',
      'custom connector',
      'tool discovery',
      'use the ryd connector',
      'standing instruction',
      'project instructions',
    ],
    questions: [
      'How do I connect ChatGPT to ryd?',
      'How do I connect Claude to ryd?',
      'Can I use Microsoft Copilot with ryd?',
      'Can I use n8n with ryd?',
      'How does my assistant know to use the ryd connector?',
      'How do I tell my assistant to use ryd?',
    ],
    faqs: [
      {
        q: 'Do I need an API key to connect my AI assistant?',
        a: 'No. Every AI platform signs you in through the ryd portal. ryd does not accept API keys for AI assistants.',
      },
      {
        q: 'How does my assistant know to use the ryd connector?',
        a: 'The ryd connector publishes its tools, each with a name and a description. Once it is switched on for the chat, the assistant matches your question to those descriptions. Naming ryd in the question makes the choice explicit.',
      },
      {
        q: 'How do I tell my assistant to use ryd?',
        a: 'Say it in the message, for example “Use the ryd connector to list my drivers” or “Don’t search the web. Only use the ryd connector.” In Claude you can also save a standing rule in preferences, a Project or a skill. The connector still has to be enabled for that chat.',
      },
    ],
  },
  '/ai-at-ryd/capabilities': {
    keywords: [
      'tools and examples',
      'ai tools',
      'mcp tools',
      'what can the assistant do',
      'fleet tools',
      'order tools',
      'write actions',
      'confirm change',
      'export download',
      'invoice pdf',
      'ryd verify',
    ],
    questions: [
      'What can my AI assistant do with ryd data?',
      'Can my AI assistant change data?',
      'How does my assistant choose a ryd tool?',
    ],
    faqs: [
      {
        q: 'Can my AI assistant change data?',
        a: 'Yes, if your company has AI write actions enabled and you ticked the permission when connecting. Every change shows a preview first and happens only after you confirm it.',
      },
    ],
  },
  '/ai-at-ryd/interface-demo': {
    keywords: [
      'interface examples',
      'ai interface demo',
      'mcp example',
      'json-rpc',
      'llms.txt',
      'skill file',
      'a2a task',
      'compare interfaces',
    ],
    questions: ['What does an MCP request look like compared to the REST API?'],
  },
  '/ai-at-ryd/security': {
    keywords: [
      'security and privacy',
      'ai security',
      'ai data protection',
      'datenschutz ki',
      'gdpr ai',
      'revoke assistant',
      'consent',
      'audit log',
      'activity log',
      'tenant isolation',
      'data residency',
      'eu data',
    ],
    questions: [
      'Is my data safe when I connect an AI assistant?',
      'How do I revoke an AI connection?',
    ],
    faqs: [
      {
        q: 'Can my AI assistant see more than I can in the portal?',
        a: 'No. It acts as you, for one company, and ryd checks your portal rights on every request. A company only ever accesses its own eligible data.',
      },
      {
        q: 'Is AI access GDPR conform?',
        a: 'Yes. Every access to personal data through AI is audit logged, and ryd processes and stores all data only within Europe.',
      },
    ],
  },
  '/ai-at-ryd/activity': {
    keywords: [
      'ai activity log',
      'ai audit log',
      'what did the assistant do',
      'mcp requests',
      'assistant history',
      'ki protokoll',
    ],
    questions: ['Which requests did AI assistants make for my company?'],
    faqs: [
      {
        q: 'Who can see the AI activity log?',
        a: 'Fleet Admins and API Credentials Admins see every request of their company; other users see their own requests. Records are kept in the EU for 400 days.',
      },
    ],
  },
  '/ai-at-ryd/developers': {
    keywords: [
      'developer guide',
      'model context protocol',
      'oauth 2.1',
      'pkce',
      'streamable http',
      'agent kit',
      'skills',
      'agents.md',
    ],
    questions: ['How does authorization work for the ryd MCP server?'],
  },
};
